<template>
  <div id="app" :class="['d-flex', {'sidebar-collapsed': isSidebarCollapsed, 'app-chromeless': chromeless}]">
    <div class="forest-bg-decor">
      <span class="blob blob-1"></span>
      <span class="blob blob-2"></span>
      <span class="blob blob-3"></span>
    </div>
    <div class="leaves-decor">
      <i class="fas fa-leaf"></i>
      <i class="fas fa-seedling"></i>
      <i class="fas fa-leaf"></i>
      <i class="fas fa-leaf"></i>
      <i class="fas fa-seedling"></i>
      <i class="fas fa-leaf"></i>
    </div>

    <!-- a stored session is being checked: a short splash instead of flashing the visitors' page -->
    <div v-if="checkingSession" class="app-splash">
      <img src="@/assets/logo.png" alt="Zerticarbon">
      <span class="app-splash-bar"></span>
    </div>

    <!-- visitors: every page full screen, no menus (they appear once signed in) -->
    <main v-else-if="chromeless" class="chromeless-main">
      <transition name="page-fade" mode="out-in">
        <router-view></router-view>
      </transition>
      <transition name="page-fade">
        <router-link v-if="showBackHome" to="/" class="back-home" title="Back to the home page">
          <i class="fas fa-arrow-left"></i><span>Home</span>
        </router-link>
      </transition>
      <button type="button" class="chromeless-theme" @click="toggleTheme" :aria-label="theme === 'dark' ? 'Switch to day' : 'Switch to night'" :title="theme === 'dark' ? 'Switch to day' : 'Switch to night'">
        <i :class="theme === 'dark' ? 'fas fa-sun' : 'fas fa-moon'"></i>
      </button>
    </main>

    <template v-else>
      <Sidebar @toggled="onSidebarToggled" />

      <div class="main-wrapper flex-grow-1 d-flex flex-column min-vh-100">
        <Header/>

        <main :class="['flex-grow-1', 'content-area', 'position-relative', { 'content-area--flush': isFlushRoute }]">
          <transition name="page-fade" mode="out-in">
            <router-view></router-view>
          </transition>
        </main>

        <Footer/>
      </div>
    </template>
  </div>
</template>

<script>
import TokenStorage from './utils/TokenStorage'
import Header from './components/structure/Header'
import Footer from './components/structure/Footer'
import Sidebar from './components/structure/Sidebar'

// A saved choice wins; otherwise follow the device's light/dark setting, falling back to dark.
function resolveInitialTheme () {
  try {
    const saved = localStorage.getItem('theme')
    if (saved === 'light' || saved === 'dark') return saved
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  } catch (e) {
    return 'dark'
  }
}

export default {
  name: 'App',

  components: {
    Header,
    Footer,
    Sidebar
  },

  data() {
    return {
      isSidebarCollapsed: false,
      // a stored session is checked before choosing between the visitors' pages and the app
      checkingSession: !!TokenStorage.get(),
      // No saved choice yet: follow the device's light/dark setting (the inline script in index.html
      // already applied it before paint); once the user toggles, that choice is saved and wins.
      theme: resolveInitialTheme()
    }
  },

  computed: {
    signedIn() {
      return this.$store.getters['user/isWeb2UserConnected'];
    },
    chromeless() {
      return !this.signedIn && !this.checkingSession;
    },
    // visitors get a way back home from every page but the home page itself and the sign-in page (it has its own)
    showBackHome() {
      return !['Home', 'Login', 'Dashboard'].includes(this.$route.name);
    },
    // Home's hero and the forests map are both full-bleed scenes — the map
    // especially, since it needs every pixel it can get. Going from one
    // flush page's edge-to-edge content straight into the other page's
    // normal 18px-padded gutter (plus Bootstrap's own container padding)
    // made the content visibly jump/shrink inward right as the page-fade
    // transition played. Keeping both routes flush removes that jump.
    isFlushRoute() {
      return (this.$route.name === 'Home' && !this.signedIn) || this.$route.name === 'Areas map';
    }
  },

  created() {
    // the header sets the theme too, but visitors' pages have no header
    document.documentElement.setAttribute('data-theme', this.theme)
    this.$store.dispatch('user/initWeb2').finally(() => { this.checkingSession = false; })
    this.$store.dispatch('accounts/initWeb3Modal')
    this.$store.dispatch('accounts/ethereumListener')

    if (localStorage.getItem('sidebar-collapsed') === 'true' || window.innerWidth < 992) {
      this.isSidebarCollapsed = true;
    }
  },

  methods: {
    onSidebarToggled(collapsed) {
      this.isSidebarCollapsed = collapsed;
    },
    toggleTheme() {
      this.theme = (document.documentElement.getAttribute('data-theme') || this.theme) === 'dark' ? 'light' : 'dark';
      localStorage.setItem('theme', this.theme);
      document.documentElement.setAttribute('data-theme', this.theme);
    }
  }
}
</script>

<style lang="scss">
@import 'assets/scss/app.scss';

#app {
  p, ul {
    &:last-child {
      margin-bottom: 0;
    }
  }
}

.main-wrapper {
  transition: margin-left 0.32s cubic-bezier(0.22, 1, 0.36, 1), width 0.32s cubic-bezier(0.22, 1, 0.36, 1);
  margin-left: 260px;
  width: calc(100% - 260px);
  background-color: transparent;
  position: relative;
  z-index: 1;
}

.sidebar-collapsed .main-wrapper {
  margin-left: 76px;
  width: calc(100% - 76px);
}

@media (max-width: 991px) {
  // The sidebar is hidden on mobile — navigation lives in the Header's
  // unified menu instead — so the content takes the full width.
  .main-wrapper {
    margin-left: 0 !important;
    width: 100% !important;
  }
}

.content-area {
  padding: 18px 18px 28px;
  background-color: transparent;
  z-index: 1;
}

@media (max-width: 576px) {
  .content-area {
    padding: 14px 10px 20px;
  }
}

// The homepage's hero is a full-bleed illustrated scene — it should touch
// the header and sidebar edges instead of sitting in the padded dashboard
// gutter every other page uses. It's also stretched to fill the column (via
// flex) so the illustrated background always reaches the footer instead of
// leaving a gap of plain page background above it on tall screens.
.content-area--flush {
  padding: 0;
  display: flex;
  flex-direction: column;

  > .home, > .areas-map {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  #home-top {
    flex: 1;
  }

  // Bootstrap's own .container-fluid gutter would otherwise still inset the
  // map by 15px on each side even with this wrapper's padding removed.
  > .areas-map {
    padding-left: 0;
    padding-right: 0;
  }
}

/* ---------------------------------------------------------------- visitors: full-screen pages, no menus */
.chromeless-main {
  position: relative;
  z-index: 1;
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;

  > .home, > .areas-map, > div {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  #home-top { flex: 1; min-height: 100vh; }

  > .areas-map { padding: 0; }

  // the map fills the whole screen (in the app it leaves room for the header and footer)
  .areas-map #map { min-height: 100vh !important; height: 100vh; }
}

.back-home,
.chromeless-theme {
  position: fixed;
  top: 16px;
  z-index: 1050;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 44px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: #fff;
  background: rgba(8, 20, 13, 0.55);
  backdrop-filter: blur(10px);
  box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.5);
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
  transition: transform 0.25s cubic-bezier(.2, .8, .2, 1), background 0.25s;

  &:hover { color: #fff; text-decoration: none; transform: translateY(-2px); background: rgba(8, 20, 13, 0.75); }
}

.back-home { left: 16px; padding: 0 18px 0 14px; }
.chromeless-theme { right: 16px; width: 44px; }

.app-splash {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 22px;
  background: var(--bg-primary-gradient, var(--bg-primary));

  img { width: 150px; animation: splash-breathe 1.6s ease-in-out infinite; }
}

.app-splash-bar {
  width: 140px;
  height: 3px;
  border-radius: 3px;
  background: linear-gradient(90deg, transparent, var(--accent-green), transparent);
  background-size: 200% 100%;
  animation: splash-slide 1.1s linear infinite;
}

@keyframes splash-breathe { 50% { transform: scale(1.05); opacity: 0.8; } }
@keyframes splash-slide { from { background-position: 200% 0; } to { background-position: -200% 0; } }

.page-fade-enter-active, .page-fade-leave-active {
  transition: opacity 0.35s cubic-bezier(0.22, 1, 0.36, 1), transform 0.35s cubic-bezier(0.22, 1, 0.36, 1);
}
.page-fade-enter, .page-fade-leave-to {
  opacity: 0;
  transform: translateY(14px) scale(0.99);
}
</style>
